/**
 * 🆕 [v13.67 — 2026-02-28] Composant : "Mes bons de travail cette saison"
 *
 * Refonte majeure du bonus (spec utilisatrice) : les employés sont dispatchés
 * sur différents BT sans équipe permanente. Le bonus est désormais calculé
 * PAR BT puis agrégé par employé.
 *
 * Ce composant consomme `mes_bts[]` retourné par `GET /api/bonus/employe/{id}`
 * et affiche pour chaque BT :
 *   - Numéro BT · Client · Chef d'équipe
 *   - Budget · Réel · Écart (h)
 *   - Mes heures · Ma part (%) · Ma contribution (h + $)
 *   - Badge de statut (Compté · À l'heure · Interne · Non-approuvé · Brouillon · Aucune heure réelle)
 *
 * Les BT non-comptés sont regroupés en fin de liste et repliés dans un
 * accordéon "Informatif" pour ne pas polluer la vue principale.
 *
 * Ref : /app/memory/RCA_V13_67_BONUS_PAR_BT.md
 */
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ClipboardList, ChevronDown, ChevronRight, TrendingUp, TrendingDown, Info } from "lucide-react";

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatH = (v) => {
  const n = Number(v ?? 0);
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(2).replace(".", ",")}h`;
};

const formatHAbs = (v) => `${Number(v ?? 0).toFixed(2).replace(".", ",")}h`;

const formatCAD = (v) => {
  const n = Number(v ?? 0);
  return n.toLocaleString("fr-CA", { style: "currency", currency: "CAD" });
};

const formatPct = (v) => `${Number(v ?? 0).toFixed(1).replace(".", ",")} %`;

// ─── Mapping badge par statut d'admissibilité BT ───────────────────────────

const STATUS_BADGE = {
  approuve: {
    label: "✓ Compté",
    className: "bg-emerald-50 border-emerald-300 text-emerald-800",
    tooltip: "Ce BT contribue à votre bonus.",
  },
  a_l_heure: {
    label: "⏱ À l'heure",
    className: "bg-blue-50 border-blue-300 text-blue-800",
    tooltip: "BT facturé à l'heure : compte pour vos 600 h d'admissibilité, mais ne génère jamais d'écart budget.",
  },
  interne: {
    label: "🏢 Interne",
    className: "bg-slate-50 border-slate-300 text-slate-700",
    tooltip: "BT interne : hors calcul du bonus.",
  },
  propose_non_approuve: {
    label: "⌛ Budget non approuvé",
    className: "bg-amber-50 border-amber-300 text-amber-800",
    tooltip: "Le budget est proposé mais n'a pas encore été officialisé — pas de calcul tant que l'approbation n'est pas faite.",
  },
  sans_budget: {
    label: "⨯ Sans budget",
    className: "bg-slate-50 border-slate-300 text-slate-600",
    tooltip: "Ce BT n'a pas de budget d'heures.",
  },
  non_termine: {
    label: "📝 Brouillon / en cours",
    className: "bg-slate-50 border-slate-300 text-slate-600",
    tooltip: "Le BT n'est pas encore marqué comme terminé.",
  },
  aucune_heure_reelle: {
    label: "🕳 Aucune heure réelle",
    className: "bg-slate-50 border-slate-300 text-slate-500",
    tooltip: "Aucune heure poinçonnée sur ce BT — le BT est ignoré pour éviter une division par zéro.",
  },
};

// ─── Composant principal ────────────────────────────────────────────────────

export function MesBonsTravailSaison({ mesBts, className }) {
  const [showInfo, setShowInfo] = useState(false);
  const [showLegende, setShowLegende] = useState(false);

  if (!Array.isArray(mesBts) || mesBts.length === 0) {
    return null;
  }

  const btsComptes = mesBts.filter((b) => b.compte_dans_bonus);
  const btsInfo = mesBts.filter((b) => !b.compte_dans_bonus);

  const totalContribution = btsComptes.reduce((acc, b) => acc + Number(b.contribution_bt || 0), 0);
  const totalHeuresMoi = mesBts.reduce((acc, b) => acc + Number(b.heures_emp_bt || 0), 0);
  const totalHeuresMoiComptes = btsComptes.reduce((acc, b) => acc + Number(b.heures_emp_bt || 0), 0);

  // Estimation $ = contribution × cout_bt pondéré (informatif — le pool progressif n'est pas linéaire)
  const totalDollarsIndicatif = btsComptes.reduce(
    (acc, b) => acc + Number(b.contribution_bt || 0) * Number(b.cout_bt || 0) * 0.6,
    0,
  );

  return (
    <Card
      className={`border-emerald-200 bg-gradient-to-br from-emerald-50/70 to-teal-50/70 ${className || ""}`}
      data-testid="card-mes-bons-travail-v13-67"
    >
      <CardHeader className="pb-2">
        <CardTitle className="text-base text-emerald-900 flex items-center gap-2">
          <ClipboardList className="w-4 h-4" />
          Mes bons de travail cette saison
          <Badge variant="outline" className="ml-1 text-xs bg-white/70">
            {btsComptes.length} compté{btsComptes.length > 1 ? "s" : ""}
            {btsInfo.length > 0 && <> · {btsInfo.length} informatif{btsInfo.length > 1 ? "s" : ""}</>}
          </Badge>
        </CardTitle>
        <div className="text-[11px] text-slate-600 mt-1">
          Chaque BT montre votre contribution personnelle : (budget − réel) × (vos heures / total heures du BT).
          Les contributions positives et négatives se compensent avant le calcul du bonus.
        </div>
      </CardHeader>
      <CardContent className="pt-2">
        {/* ─── Table des BT comptés ─── */}
        {btsComptes.length > 0 && (
          <div className="overflow-x-auto -mx-2" data-testid="mes-bts-comptes-table-wrap">
            <table className="w-full text-[12px] border-collapse" data-testid="mes-bts-comptes-table">
              <thead>
                <tr className="border-b border-emerald-200 text-emerald-900">
                  <th className="text-left px-2 py-1.5 font-semibold">BT</th>
                  <th className="text-left px-2 py-1.5 font-semibold">Client</th>
                  <th className="text-left px-2 py-1.5 font-semibold">Chef</th>
                  <th className="text-right px-2 py-1.5 font-semibold">Budget</th>
                  <th className="text-right px-2 py-1.5 font-semibold">Réel</th>
                  <th className="text-right px-2 py-1.5 font-semibold">Écart</th>
                  <th className="text-right px-2 py-1.5 font-semibold">Mes h</th>
                  <th className="text-right px-2 py-1.5 font-semibold">Ma part</th>
                  <th className="text-right px-2 py-1.5 font-semibold">Ma contrib.</th>
                  <th className="text-right px-2 py-1.5 font-semibold" title="Indicatif : contribution × coût pondéré × 60%. Le vrai calcul utilise un pool progressif par tranche.">
                    ~ $
                  </th>
                </tr>
              </thead>
              <tbody>
                {btsComptes.map((b) => {
                  const contrib = Number(b.contribution_bt || 0);
                  const contribClass =
                    contrib > 0.005
                      ? "text-emerald-700 font-semibold"
                      : contrib < -0.005
                        ? "text-red-600 font-semibold"
                        : "text-slate-500";
                  const ecart = Number(b.ecart_bt ?? 0);
                  const ecartClass =
                    ecart > 0.005 ? "text-emerald-700" : ecart < -0.005 ? "text-red-600" : "text-slate-500";
                  const dollarsIndicatif = contrib * Number(b.cout_bt || 0) * 0.6;
                  return (
                    <tr
                      key={b.bt_id}
                      className="border-b border-emerald-100 hover:bg-white/60"
                      data-testid={`mes-bts-row-${b.bt_id}`}
                    >
                      <td className="px-2 py-1.5 font-mono text-[11px] text-slate-700 whitespace-nowrap">
                        {b.numero_bt || b.bt_id.slice(0, 8)}
                      </td>
                      <td className="px-2 py-1.5 text-slate-700 truncate max-w-[140px]" title={b.client_nom}>
                        {b.client_nom || "—"}
                      </td>
                      <td className="px-2 py-1.5 text-slate-600 truncate max-w-[120px]" title={b.chef_equipe_nom}>
                        {b.chef_equipe_nom || "—"}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-slate-700">
                        {formatHAbs(b.heures_budgetees_bt)}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-slate-700">
                        {formatHAbs(b.heures_reelles_bt)}
                      </td>
                      <td className={`px-2 py-1.5 text-right tabular-nums ${ecartClass}`}>
                        {formatH(b.ecart_bt)}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-slate-800 font-medium">
                        {formatHAbs(b.heures_emp_bt)}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-slate-600">
                        {formatPct(b.part_emp_bt_pct)}
                      </td>
                      <td className={`px-2 py-1.5 text-right tabular-nums ${contribClass}`} data-testid={`contrib-${b.bt_id}`}>
                        {formatH(b.contribution_bt)}
                      </td>
                      <td className={`px-2 py-1.5 text-right tabular-nums ${contribClass} whitespace-nowrap`}>
                        {formatCAD(dollarsIndicatif)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-emerald-300 bg-emerald-100/40">
                  <td colSpan={6} className="px-2 py-1.5 text-right text-emerald-900 font-semibold">
                    Total ({btsComptes.length} BT)
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-emerald-900 font-semibold" data-testid="mes-bts-total-heures-moi">
                    {formatHAbs(totalHeuresMoiComptes)}
                  </td>
                  <td className="px-2 py-1.5" />
                  <td
                    className={`px-2 py-1.5 text-right tabular-nums font-bold ${totalContribution > 0.005 ? "text-emerald-800" : totalContribution < -0.005 ? "text-red-700" : "text-slate-600"}`}
                    data-testid="mes-bts-total-contribution"
                  >
                    {formatH(totalContribution)}
                  </td>
                  <td
                    className="px-2 py-1.5 text-right tabular-nums font-bold text-emerald-800"
                    data-testid="mes-bts-total-dollars-indicatif"
                  >
                    ~ {formatCAD(totalDollarsIndicatif)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {btsComptes.length === 0 && (
          <div className="text-xs text-slate-500 italic px-2 py-3 bg-white/50 rounded" data-testid="mes-bts-aucun-compte">
            Aucun BT admissible pour cette saison. Regardez la liste informative ci-dessous pour comprendre.
          </div>
        )}

        {/* ─── Légende badges ─── */}
        <div className="mt-3">
          <button
            type="button"
            className="text-[11px] text-emerald-800 hover:underline flex items-center gap-1"
            onClick={() => setShowLegende((v) => !v)}
            data-testid="mes-bts-toggle-legende"
          >
            {showLegende ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            Légende des statuts
          </button>
          {showLegende && (
            <div className="mt-1 flex flex-wrap gap-1.5" data-testid="mes-bts-legende">
              {Object.entries(STATUS_BADGE).map(([key, cfg]) => (
                <Badge
                  key={key}
                  variant="outline"
                  className={`text-[10px] ${cfg.className}`}
                  title={cfg.tooltip}
                  data-testid={`mes-bts-legende-${key}`}
                >
                  {cfg.label}
                </Badge>
              ))}
            </div>
          )}
        </div>

        {/* ─── BT informatifs (non-comptés) ─── */}
        {btsInfo.length > 0 && (
          <div className="mt-3 border-t border-emerald-200 pt-2">
            <button
              type="button"
              className="w-full text-left text-[12px] font-medium text-slate-700 hover:text-slate-900 flex items-center gap-2"
              onClick={() => setShowInfo((v) => !v)}
              data-testid="mes-bts-toggle-info"
            >
              {showInfo ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              <Info className="w-3 h-3 text-slate-500" />
              BT informatifs (non comptés dans le bonus) — {btsInfo.length}
            </button>
            {showInfo && (
              <div className="mt-2 space-y-1" data-testid="mes-bts-info-list">
                {btsInfo.map((b) => {
                  const badge = STATUS_BADGE[b.budget_status] || STATUS_BADGE.non_termine;
                  return (
                    <div
                      key={b.bt_id}
                      className="flex items-center gap-2 px-2 py-1.5 bg-white/60 border border-slate-200 rounded text-[11px]"
                      data-testid={`mes-bts-info-row-${b.bt_id}`}
                    >
                      <span className="font-mono text-slate-600">{b.numero_bt || b.bt_id.slice(0, 8)}</span>
                      <span className="text-slate-700 truncate flex-1" title={b.client_nom}>
                        {b.client_nom || "—"}
                      </span>
                      <span className="text-slate-500 tabular-nums whitespace-nowrap">
                        Mes h : {formatHAbs(b.heures_emp_bt)}
                      </span>
                      <Badge
                        variant="outline"
                        className={`text-[10px] ${badge.className}`}
                        title={badge.tooltip}
                        data-testid={`mes-bts-info-badge-${b.bt_id}`}
                      >
                        {badge.label}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ─── Note pédagogique ─── */}
        <div className="mt-3 text-[11px] text-slate-600 leading-snug bg-white/50 rounded px-2 py-1.5 border border-emerald-100">
          <strong>💡 Comment lire ce tableau ?</strong> Pour chaque BT compté, votre <em>contribution</em> est
          (budget − réel) × (vos heures / total heures du BT). Un BT en dépassement génère une contribution négative
          qui peut être compensée par les BT sous-budget. Le montant en $ affiché est <em>indicatif</em> — le calcul
          réel du bonus utilise un pool progressif par tranche d'heures économisées.
        </div>
      </CardContent>
    </Card>
  );
}
